/**
 * Chat State Hook
 *
 * Manages conversation state for the chat panel.
 */

import type { ChatMessage } from '@luminaria/shared';
import { useState, useCallback } from 'react';

/** A panel message: a wire `ChatMessage` (@luminaria/shared) plus UI state (view model). */
export interface ChatPanelMessage extends ChatMessage {
  /** A locally generated error notice, not a real assistant turn — never sent back as history. */
  isError?: boolean;
}

export interface UseChatStateResult {
  messages: ChatPanelMessage[];
  addMessage: (message: ChatPanelMessage) => void;
  clearMessages: () => void;
}

export const useChatState = (): UseChatStateResult => {
  const [messages, setMessages] = useState<ChatPanelMessage[]>([]);

  const addMessage = useCallback((message: ChatPanelMessage) => {
    setMessages((prev) => [...prev, message]);
  }, []);

  const clearMessages = useCallback(() => {
    setMessages([]);
  }, []);

  return {
    messages,
    addMessage,
    clearMessages,
  };
};
